// Block Blast — Puzzle: Teile aufs 8x8-Brett legen, volle Reihen und Spalten
// werden geräumt. Voxel-Materialien wie bei Stack, alles prozedural.
// Steuerung: Teil aus der Ablage ziehen (oder antippen und dann Zielfeld
// antippen). Runde endet, wenn kein Teil mehr passt.
import { useRef, useEffect } from "react";
import { drawTexturedBlock, drawBlockText, drawCoin, setupCanvas } from "./pixelArt";

const H = 640;
const BASE_ASPECT = 420 / 640;
const GRID = 8;
const CELL = 42; // Brett: 336 x 336
const BOARD_TOP = 88;
const TRAY_CELL = 20; // Teile in der Ablage verkleinert
const COIN_PIECE_CHANCE = 0.06; // selten trägt ein Teil eine Münze

const MATERIALS = [
  { base: "#d93a2b", light: "#f06451", dark: "#a82417" }, // Rot
  { base: "#57b9e8", light: "#7ecdf2", dark: "#3d97c4" }, // Blau
  { base: "#e8c34e", light: "#f2d67a", dark: "#c4a136" }, // Gelb
  { base: "#6abe30", light: "#7ed641", dark: "#4e9420" }, // Grün
  { base: "#9b6ee8", light: "#b48ff2", dark: "#7a4ec4" }, // Lila
  { base: "#e8823a", light: "#f29c5c", dark: "#c4661f" }, // Orange
];

// Teile als String-Raster ("x" = Block). Klassischer Block-Blast-Mix.
const SHAPES = [
  ["x"],
  ["xx"],
  ["xxx"],
  ["xxxx"],
  ["xxxxx"],
  ["x", "x"],
  ["x", "x", "x"],
  ["x", "x", "x", "x"],
  ["xx", "xx"],
  ["xxx", "xxx", "xxx"],
  ["x.", "xx"],
  ["xx", "x."],
  ["xx", ".x"],
  ["x.", "x.", "xx"],
  ["xx", "x.", "x."],
  ["xx", ".x", ".x"],
  ["x..", "x..", "xxx"],
  ["xxx", "x..", "x.."],
  ["xxx", "..x", "..x"],
];

const shapeToCells = (rows) => {
  const cells = [];
  for (let r = 0; r < rows.length; r++) {
    for (let c = 0; c < rows[r].length; c++) {
      if (rows[r][c] === "x") cells.push({ dx: c, dy: r });
    }
  }
  return cells;
};

export default function BlockBlastGame({ onGameOver, onStart, autoStart = false }) {
  const canvasRef = useRef(null);
  const onGameOverRef = useRef(onGameOver);
  onGameOverRef.current = onGameOver;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  useEffect(() => {
    const canvas = canvasRef.current;
    // Spielfeldbreite ans Seitenverhältnis des Containers anpassen (wie bei
    // den anderen Spielen): Brett bleibt mittig, links/rechts wird Hintergrund.
    const availW = canvas.parentElement?.clientWidth || 420;
    const availH = canvas.parentElement?.clientHeight || H;
    const aspect = Math.min(Math.max(availW / availH, BASE_ASPECT), 3.2);
    const W = Math.round(H * aspect);
    const ctx = setupCanvas(canvas, W, H);

    const boardX = (W - GRID * CELL) / 2;
    const trayY = BOARD_TOP + GRID * CELL + 44; // Mitte der Ablage-Zeile
    const traySlotX = (i) => W / 2 + (i - 1) * Math.min(150, W / 3);

    const state = {
      phase: "ready", // ready | running | over
      grid: [], // [row][col] = null | { mat, coin }
      tray: [null, null, null],
      selected: -1,
      dragging: false,
      pointer: { x: 0, y: 0 },
      score: 0,
      coins: 0,
    };

    const newPiece = () => {
      const rows = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      const cells = shapeToCells(rows);
      return {
        cells,
        w: Math.max(...cells.map((c) => c.dx)) + 1,
        h: Math.max(...cells.map((c) => c.dy)) + 1,
        mat: Math.floor(Math.random() * MATERIALS.length),
        // Münze sitzt auf einem zufälligen Block des Teils — sie zählt erst,
        // wenn ihre Reihe/Spalte geräumt wird
        coinIdx:
          Math.random() < COIN_PIECE_CHANCE
            ? Math.floor(Math.random() * cells.length)
            : -1,
      };
    };

    const refillTray = () => {
      if (state.tray.every((p) => p === null)) {
        state.tray = [newPiece(), newPiece(), newPiece()];
      }
    };

    const canPlaceAt = (piece, col, row) => {
      for (const c of piece.cells) {
        const x = col + c.dx;
        const y = row + c.dy;
        if (x < 0 || x >= GRID || y < 0 || y >= GRID) return false;
        if (state.grid[y][x]) return false;
      }
      return true;
    };

    const anyMoveLeft = () =>
      state.tray.some((piece) => {
        if (!piece) return false;
        for (let r = 0; r <= GRID - piece.h; r++) {
          for (let c = 0; c <= GRID - piece.w; c++) {
            if (canPlaceAt(piece, c, r)) return true;
          }
        }
        return false;
      });

    // Zielzelle unter dem Zeiger: Teil hängt mittig am Cursor
    const ghostCell = (piece) => {
      const col = Math.round((state.pointer.x - boardX) / CELL - piece.w / 2);
      const row = Math.round((state.pointer.y - BOARD_TOP) / CELL - piece.h / 2);
      return { col, row };
    };

    const clearLines = () => {
      const fullRows = [];
      const fullCols = [];
      for (let r = 0; r < GRID; r++) {
        if (state.grid[r].every(Boolean)) fullRows.push(r);
      }
      for (let c = 0; c < GRID; c++) {
        if (state.grid.every((row) => row[c])) fullCols.push(c);
      }
      const total = fullRows.length + fullCols.length;
      if (total === 0) return;
      const collect = (cell) => {
        if (cell && cell.coin) state.coins++;
      };
      for (const r of fullRows) {
        for (let c = 0; c < GRID; c++) {
          collect(state.grid[r][c]);
          state.grid[r][c] = null;
        }
      }
      for (const c of fullCols) {
        for (let r = 0; r < GRID; r++) {
          collect(state.grid[r][c]);
          state.grid[r][c] = null;
        }
      }
      // 10 Punkte pro Linie, kleiner Bonus für Mehrfach-Räumungen
      state.score += total * 10 + (total - 1) * 5;
    };

    const tryPlace = (pieceIdx) => {
      const piece = state.tray[pieceIdx];
      if (!piece) return false;
      const { col, row } = ghostCell(piece);
      if (!canPlaceAt(piece, col, row)) return false;
      piece.cells.forEach((c, i) => {
        state.grid[row + c.dy][col + c.dx] = {
          mat: piece.mat,
          coin: i === piece.coinIdx,
        };
      });
      state.score += piece.cells.length;
      state.tray[pieceIdx] = null;
      state.selected = -1;
      clearLines();
      refillTray();
      if (!anyMoveLeft()) {
        state.phase = "over";
        if (onGameOverRef.current)
          onGameOverRef.current(state.score, state.coins);
      }
      return true;
    };

    const reset = () => {
      state.grid = Array.from({ length: GRID }, () => Array(GRID).fill(null));
      state.tray = [newPiece(), newPiece(), newPiece()];
      state.selected = -1;
      state.dragging = false;
      state.score = 0;
      state.coins = 0;
    };

    const start = () => {
      reset();
      state.phase = "running";
      if (onStartRef.current) onStartRef.current();
    };

    // Zeiger-Koordinaten in logische Canvas-Koordinaten umrechnen
    // (object-fit: contain kann Letterboxing erzeugen)
    const toLocal = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scale = Math.min(rect.width / W, rect.height / H);
      const offX = rect.left + (rect.width - W * scale) / 2;
      const offY = rect.top + (rect.height - H * scale) / 2;
      return { x: (e.clientX - offX) / scale, y: (e.clientY - offY) / scale };
    };

    const trayHit = (x, y) => {
      for (let i = 0; i < 3; i++) {
        if (!state.tray[i]) continue;
        if (Math.abs(x - traySlotX(i)) < 70 && Math.abs(y - trayY) < 60)
          return i;
      }
      return -1;
    };

    const onPointerDown = (e) => {
      e.preventDefault();
      if (state.phase !== "running") {
        start();
        return;
      }
      const p = toLocal(e);
      state.pointer = p;
      const hit = trayHit(p.x, p.y);
      if (hit !== -1) {
        state.selected = hit;
        state.dragging = true;
        canvas.setPointerCapture?.(e.pointerId);
        return;
      }
      // Teil bereits gewählt (Tippen-Modus): auf dem Brett ablegen
      if (state.selected !== -1) tryPlace(state.selected);
    };

    const onPointerMove = (e) => {
      const p = toLocal(e);
      // Beim Ziehen mit dem Finger das Teil etwas über dem Finger anzeigen
      if (state.dragging && e.pointerType === "touch") p.y -= 50;
      state.pointer = p;
    };

    const onPointerUp = (e) => {
      if (!state.dragging) return;
      state.dragging = false;
      const placed = tryPlace(state.selected);
      // Nicht platziert → Teil bleibt gewählt (Tippen-Modus als Fallback)
      if (placed) state.selected = -1;
    };

    const drawCellBlock = (x, y, size, mat, seed) => {
      const m = MATERIALS[mat];
      drawTexturedBlock(ctx, x, y, size, size, m.base, m.light, m.dark, seed);
    };

    const drawPieceAt = (piece, px, py, cell, alpha = 1) => {
      ctx.globalAlpha = alpha;
      piece.cells.forEach((c, i) => {
        drawCellBlock(px + c.dx * cell, py + c.dy * cell, cell, piece.mat, i * 31 + 7);
        if (i === piece.coinIdx) {
          drawCoin(ctx, px + c.dx * cell + cell * 0.2, py + c.dy * cell + cell * 0.2, cell * 0.6);
        }
      });
      ctx.globalAlpha = 1;
    };

    const draw = () => {
      // Hintergrund: tiefes Blau, unten dunkler
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#1b2740");
      grad.addColorStop(1, "#0d111c");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Brett (zweifarbiges Schachmuster mit Rahmen)
      ctx.fillStyle = "#0a0e16";
      ctx.fillRect(boardX - 6, BOARD_TOP - 6, GRID * CELL + 12, GRID * CELL + 12);
      for (let r = 0; r < GRID; r++) {
        for (let c = 0; c < GRID; c++) {
          ctx.fillStyle = (r + c) % 2 === 0 ? "#1c2733" : "#202e3f";
          ctx.fillRect(boardX + c * CELL, BOARD_TOP + r * CELL, CELL, CELL);
        }
      }

      // Gelegte Blöcke
      for (let r = 0; r < GRID; r++) {
        for (let c = 0; c < GRID; c++) {
          const cell = state.grid[r][c];
          if (!cell) continue;
          drawCellBlock(boardX + c * CELL, BOARD_TOP + r * CELL, CELL, cell.mat, r * 8 + c);
          if (cell.coin) {
            drawCoin(ctx, boardX + c * CELL + CELL * 0.2, BOARD_TOP + r * CELL + CELL * 0.2, CELL * 0.6);
          }
        }
      }

      // Geist-Vorschau des gewählten Teils
      if (state.phase === "running" && state.selected !== -1) {
        const piece = state.tray[state.selected];
        if (piece) {
          const { col, row } = ghostCell(piece);
          const valid = canPlaceAt(piece, col, row);
          if (state.dragging) {
            // Teil folgt dem Zeiger frei
            drawPieceAt(
              piece,
              state.pointer.x - (piece.w * CELL) / 2,
              state.pointer.y - (piece.h * CELL) / 2,
              CELL,
              0.85
            );
          }
          if (valid) {
            ctx.globalAlpha = 0.35;
            piece.cells.forEach((c) => {
              ctx.fillStyle = "#ffffff";
              ctx.fillRect(
                boardX + (col + c.dx) * CELL,
                BOARD_TOP + (row + c.dy) * CELL,
                CELL,
                CELL
              );
            });
            ctx.globalAlpha = 1;
          }
        }
      }

      // Ablage (3 Slots)
      for (let i = 0; i < 3; i++) {
        const piece = state.tray[i];
        const cx = traySlotX(i);
        ctx.fillStyle = state.selected === i ? "#22304a" : "#141b28";
        ctx.fillRect(cx - 70, trayY - 58, 140, 116);
        if (piece && !(state.dragging && state.selected === i)) {
          drawPieceAt(
            piece,
            cx - (piece.w * TRAY_CELL) / 2,
            trayY - (piece.h * TRAY_CELL) / 2,
            TRAY_CELL
          );
        }
      }

      // HUD
      drawBlockText(ctx, `${state.score}`, W / 2, 46, 36);
      drawCoin(ctx, 12, 16, 20);
      drawBlockText(ctx, `${state.coins}`, 48, 27, 20);

      if (state.phase === "ready") {
        ctx.fillStyle = "rgba(8, 12, 20, 0.55)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "BLOCK BLAST", W / 2, H / 2 - 40, 40);
        drawBlockText(ctx, "Tippen zum Start", W / 2, H / 2 + 10, 16);
      } else if (state.phase === "over") {
        ctx.fillStyle = "rgba(8, 12, 20, 0.55)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "GAME OVER", W / 2, H / 2 - 40, 40);
        drawBlockText(ctx, `${state.score} Punkte`, W / 2, H / 2 + 5, 24);
        drawBlockText(ctx, "Tippen für Neustart", W / 2, H / 2 + 45, 15);
      }
    };

    let raf;
    const loop = () => {
      draw();
      raf = requestAnimationFrame(loop);
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);

    reset();
    if (autoStart) start(); // Start-Button auf der Seite: direkt losspielen
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
    };
  }, [autoStart]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full touch-none select-none"
      style={{ imageRendering: "pixelated", objectFit: "contain" }}
    />
  );
}
